import type { UploadedPdfInfo, SignPdfResponse, SigningField } from '../types/fields';

const API_BASE = '/api';

export type UploadResponse = UploadedPdfInfo;

export interface UploadProgress {
  percent: number;
  loaded: number;
  total: number;
}

export async function uploadPdf(
  file: File,
  onProgress?: (progress: UploadProgress) => void,
  signal?: AbortSignal
): Promise<UploadResponse> {
  return uploadPdfViaMultipart(file, onProgress, signal);
}

function uploadPdfViaMultipart(
  file: File,
  onProgress?: (progress: UploadProgress) => void,
  signal?: AbortSignal
): Promise<UploadResponse> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const url = `${API_BASE}/pdf/upload`;

    xhr.open('POST', url, true);

    const abortHandler = () => {
      xhr.abort();
      reject(new DOMException('The upload was aborted.', 'AbortError'));
    };

    if (signal) {
      if (signal.aborted) {
        abortHandler();
        return;
      }
      signal.addEventListener('abort', abortHandler, { once: true });
    }

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100)));
        onProgress({ percent, loaded: event.loaded, total: event.total });
      }
    };

    xhr.onload = () => {
      if (signal) {
        signal.removeEventListener('abort', abortHandler);
      }
      // Ignore late responses once the caller has aborted — prevents a cancel/complete race
      // from resolving the upload promise after the user clicked Cancel.
      if (signal?.aborted) {
        reject(new DOMException('The upload was aborted.', 'AbortError'));
        return;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText || '{}');
          resolve(data as UploadResponse);
        } catch {
          reject(new Error('Invalid server response during upload'));
        }
      } else {
        const text = xhr.responseText || `Upload failed with status ${xhr.status}`;
        reject(new Error(text));
      }
    };

    xhr.onerror = () => {
      if (signal) {
        signal.removeEventListener('abort', abortHandler);
      }
      reject(new Error('Network error during PDF upload'));
    };

    xhr.onabort = () => {
      if (signal) {
        signal.removeEventListener('abort', abortHandler);
      }
      reject(new DOMException('The upload was aborted.', 'AbortError'));
    };

    const formData = new FormData();
    formData.append('pdf', file);
    xhr.send(formData);
  });
}

export async function signPdf(
  fileIdOrFile: string | File,
  fields: SigningField[],
  signal?: AbortSignal
): Promise<SignPdfResponse> {
  const isDirectFile = typeof File !== 'undefined' && fileIdOrFile instanceof File;

  if (isDirectFile) {
    const file = fileIdOrFile as File;
    const form = new FormData();
    form.append('pdf', file);
    form.append('fields', JSON.stringify(fields));
    const res = await fetch(`${API_BASE}/pdf/sign`, {
      method: 'POST',
      body: form,
      signal,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText);
      throw new Error(text || `Signing failed with status ${res.status}`);
    }
    const data = (await res.json()) as SignPdfResponse;
    if (data) {
      (data as SignPdfResponse & { downloadUrl: string }).downloadUrl = resolveDownloadUrl(data);
    }
    return data;
  }

  const fileId = fileIdOrFile as string;
  const res = await fetch(`${API_BASE}/pdf/sign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileId, fields }),
    signal,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `Signing failed with status ${res.status}`);
  }
  const data = (await res.json()) as SignPdfResponse;
  if (data) {
    (data as SignPdfResponse & { downloadUrl: string }).downloadUrl = resolveDownloadUrl(data);
  }
  return data;
}

export function getDownloadUrl(signedFileId: string, filename?: string | null): string {
  const base = `${API_BASE}/pdf/download/${encodeURIComponent(signedFileId)}`;
  if (!filename) return base;
  return `${base}?filename=${encodeURIComponent(filename)}`;
}

export function getSourceUrl(fileId: string): string {
  return `${API_BASE}/pdf/source/${encodeURIComponent(fileId)}`;
}

export async function fetchFreshDownloadUrl(
  signedFileId: string,
  filename?: string | null
): Promise<string> {
  const params = filename ? `?filename=${encodeURIComponent(filename)}` : '';
  try {
    const res = await fetch(
      `${API_BASE}/pdf/download-url/${encodeURIComponent(signedFileId)}${params}`
    );
    if (res.ok) {
      const data = (await res.json()) as { downloadUrl?: string };
      if (data.downloadUrl) return data.downloadUrl;
    }
  } catch (e) {
    // Network/DNS failure — fall back to the deterministic download URL below.
    console.warn('Failed to fetch fresh download URL:', e);
  }
  return getDownloadUrl(signedFileId, filename);
}

export async function fetchFreshSourceUrl(fileId: string): Promise<string> {
  try {
    const res = await fetch(`${API_BASE}/pdf/source-url/${encodeURIComponent(fileId)}`);
    if (res.ok) {
      const data = (await res.json()) as { sourceUrl?: string };
      if (data.sourceUrl) return data.sourceUrl;
    }
  } catch (e) {
    // Network/DNS failure — fall back to the deterministic source URL below.
    console.warn('Failed to fetch fresh source URL:', e);
  }
  return getSourceUrl(fileId);
}

export function resolveDownloadUrl(response: SignPdfResponse): string {
  if (response && typeof response.downloadUrl === 'string' && response.downloadUrl.length > 0) {
    return response.downloadUrl;
  }
  return getDownloadUrl(response?.signedFileId || '', response?.originalName);
}

export async function cleanupFiles(): Promise<{ deleted: number }> {
  const res = await fetch(`${API_BASE}/pdf/cleanup`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    throw new Error('Cleanup request failed');
  }
  return res.json();
}

export async function checkHealth(): Promise<{ ok: boolean }> {
  const res = await fetch(`${API_BASE}/health`);
  return res.json();
}

export interface DocumentMutationResponse {
  fileId: string;
  pageCount: number;
  pageSizes: Array<{ width: number; height: number; rotation?: number }>;
  pageIndexMap: number[];
  sourceUrl?: string;
}

export async function mergePdf(
  baseFileId: string,
  appendFileId: string
): Promise<DocumentMutationResponse> {
  const res = await fetch(`${API_BASE}/pdf/merge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ baseFileId, appendFileId }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || 'Merge failed');
  }
  return res.json();
}

export async function mergePdfDirect(
  basePdf: File,
  appendPdf: File
): Promise<DocumentMutationResponse> {
  const form = new FormData();
  form.append('basePdf', basePdf);
  form.append('appendPdf', appendPdf);
  const res = await fetch(`${API_BASE}/pdf/merge`, { method: 'POST', body: form });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || 'Merge failed');
  }
  return res.json();
}

export async function fetchWorkingPdfBlob(fileId: string, sourceUrl?: string): Promise<Blob> {
  const url =
    sourceUrl && sourceUrl.length > 0 ? sourceUrl : getSourceUrl(fileId);
  const res = await fetch(url);
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || 'Failed to load working PDF');
  }
  return res.blob();
}

export async function mutatePdfPages(
  fileId: string,
  operation: Record<string, unknown>
): Promise<DocumentMutationResponse> {
  const res = await fetch(`${API_BASE}/pdf/pages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileId, operation }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || 'Page operation failed');
  }
  return res.json();
}

export async function mutatePdfPagesDirect(
  pdfFile: File,
  operation: Record<string, unknown>
): Promise<DocumentMutationResponse> {
  const form = new FormData();
  form.append('pdf', pdfFile);
  form.append('operation', JSON.stringify(operation));
  const res = await fetch(`${API_BASE}/pdf/pages`, { method: 'POST', body: form });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || 'Page operation failed');
  }
  return res.json();
}